"use client";

import { cn } from "@/lib/tailwind";
import { useState, useEffect } from "react";
import { SizeAndFit } from "../../../types";
import MeasurementTab from "./MeasurementTab";
import SizeGuide from "./SizeGuide";
import { usePdpContext } from "@/providers/PdpProvider";
import usePdpQueryParams from "@/hooks/usePdpQueryParams";

const SizeNFit = ({
  data,
  lang,
  category,
  currentTab,
  categoryURL,
  layoutDetails,
  id,
  handleUpdateContentHeight
}: SizeAndFit & { categoryURL: string } & { id?: string } & {
  handleUpdateContentHeight?: () => void;
}) => {
  const { updatePdpQueryParams } = usePdpQueryParams();
  const { pdpData, setPdpData } = usePdpContext();

  // Determine initial tab: context takes priority over prop
  const getInitialTab = () => {
    if (pdpData?.sizeAndFitChildTab !== undefined) return pdpData.sizeAndFitChildTab;
    return currentTab ?? 0;
  };

  const [selected, setSelected] = useState(getInitialTab());

  // Sync whenever the context value changes (e.g. "Size Guide" button on PDP clicked)
  useEffect(() => {
    if (pdpData?.sizeAndFitChildTab !== undefined) {
      setSelected(pdpData.sizeAndFitChildTab);
    }
  }, [pdpData?.sizeAndFitChildTab]);

  // Fallback: sync from prop when context has no opinion
  useEffect(() => {
    if (pdpData?.sizeAndFitChildTab === undefined && currentTab !== undefined && currentTab !== selected) {
      setSelected(currentTab);
    }
  }, [currentTab]);

  const tabs = [
    {
      label: data?.measurementsTab?.tabTitle,
      key: "measurements",
      content: (
        <MeasurementTab
          {...data?.measurementsTab}
          images={data?.images}
          bridgeSize={data?.bridgeSize}
          lensSize={data?.lensSize}
          tSize={data?.tSize}
          currentFit={data?.fit}
          layoutDetails={layoutDetails}
        />
      ),
    },
    {
      label: data?.sizeGuideTab?.tabTitle,
      key: "sizeGuide",
      content: (
        <SizeGuide
          {...data?.sizeGuideTab}
          lang={lang}
          category={category}
          categoryURL={categoryURL}
        />
      ),
    },
  ];

  const handleClick = (index: number) => {
    setSelected(index);
    setPdpData({ ...pdpData, sizeAndFitChildTab: index });
    // Update URL parameter to reflect the selected child tab
    updatePdpQueryParams({ childTab: tabs[index].key }, {});
    handleUpdateContentHeight?.()
  };

  return (
    <div id={id} className="container px-4 md:px-12 text-sm md:text-base">
      <div className="flex items-center justify-center py-3 font-semibold lg:justify-start">
        {tabs?.map((item, index) => (
          <button
            key={index}
            className={cn(
              "px-5 py-2 text-sm sm:w-44",
              selected === index ? "bg-primary-3 text-white" : "bg-tertiary-5 text-black",
            )}
            onClick={() => handleClick(index)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className="flex h-full flex-col justify-center lg:flex-row">
        {tabs[selected]?.content}
      </div>
    </div>
  );
};

export default SizeNFit;
